import { useEffect } from 'react';

import { useFocus } from '@/hooks';

const InputGroup = ({
  value,
  error,
  caption,
  onChange,
  onSubmit,
  allowPaste,
  isSubmitting,
  submittingCaption,
  ...rest
}) => {
  const canPaste = allowPaste && !value;

  let _caption = caption;

  if (error && value) {
    _caption = 'Clear';
  } else if (canPaste) {
    _caption = 'Paste';
  } else if (isSubmitting) {
    _caption = `${submittingCaption}...`;
  }

  const inputRef = useFocus();

  const focusOnInput = () => inputRef.current?.focus();

  useEffect(() => {
    if (error) {
      focusOnInput();
    }
  }, [error]);

  // We have to call onSubmit() with a value because the handler
  // functions were updated to expect a value. This is because
  // in instances where onSubmit() is fired immediately after
  // onChange() the state change does not propagate by the
  // time the onSubmit() handler executes.

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onSubmit(value);
    }
  };

  const handleSubmit = async () => {
    if (error) {
      focusOnInput();

      if (value) {
        inputRef.current.value = '';
        onChange('');
      }
    } else if (canPaste) {
      try {
        const text = (await navigator.clipboard.readText()).trim();

        if (text) {
          onChange(text);
          onSubmit(text); // proceed immediately
        } else {
          focusOnInput();
        }
      } catch (err) {
        console.error('Failed to read clipboard: ', err);
      }
    } else {
      onSubmit(value);
    }
  };

  return (
    <div className="input-group">
      <input
        ref={inputRef}
        {...{ value, ...rest }}
        onKeyDown={handleKeyDown}
        onChange={(e) => onChange(e.target.value)}
        className={`form-control shadow-sm ${error ? 'border-danger is-invalid' : 'border'}`}
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        className={`btn shadow-sm ${error ? 'btn-outline-danger' : 'border'}`}
      >
        {_caption}
      </button>
    </div>
  );
};

export default InputGroup;
